<template>
    <div
        :class="[
            'radio',
            {
                'radio--inline': !isDisplayBlock,
                'radio--disabled': props.disabled,
            },
        ]"
        :style="props.style"
    >
        <label
            :class="[
                'radio__label',
                {
                    'radio__label--disabled': props.disabled,
                },
            ]"
        >
            <input
                type="radio"
                class="radio__input"
                v-bind="$attrs"
                :name="props.name"
                :value="props.value"
                :checked="props.value === props.modelValue"
                :disabled="props.disabled"
                @change="handleChange"
            />
            <slot></slot>{{ label }}
        </label>
    </div>
    <ErrorMessage
        v-if="props.hasError && props.name"
        :name="props.name"
        class="error-message"
        as="div"
    />
</template>

<script setup lang="ts">
import {ErrorMessage} from 'vee-validate';

export type RadioValueT = null | string;

export interface RadioPropsI {
    label?: string;
    value?: RadioValueT;
    disabled?: boolean;
}

export interface PropsI {
    name?: string;
    label?: string;
    value?: RadioValueT;
    modelValue?: RadioValueT;
    disabled?: boolean;
    isDisplayBlock?: boolean;
    hasError?: boolean;
    style?: any;
}

const emits = defineEmits<{
    (e: 'update:modelValue', value?: RadioValueT): void;
    (e: 'change', event: Event): void;
}>();

const handleChange = (e: Event) => {
    emits('update:modelValue', props.value);
    emits('change', e);
};

const props = withDefaults(defineProps<PropsI>(), {
    isDisplayBlock: false,
});
</script>

<style lang="scss" scoped>
.radio {
    display: flex;
    align-items: center;
    padding-left: 0;

    &--inline {
        display: inline-flex;
        margin-right: 24px;
    }

    &--disabled {
        opacity: 0.6;
    }
}

.radio__label {
    position: relative;
    display: flex;
    align-items: center;
    line-height: 24px;
    cursor: pointer;

    &--disabled {
        cursor: not-allowed;
    }
}

.radio__input {
    display: flex;
    justify-content: center;
    align-items: center;
    width: 17px;
    height: 17px;
    margin-right: 8px;
    border: 1.5px solid $black_08;
    border-radius: 50%;
    appearance: none;
    cursor: inherit;

    &:before {
        content: '';
        width: 9px;
        height: 9px;
        background-color: $primary;
        border-radius: 50%;
        transform: scale(0);
        transition: 120ms transform ease-in-out;
    }

    &:checked {
        border-color: $primary;

        &:before {
            transform: scale(1);
        }
    }

    &:focus {
        box-shadow: 0 0 5px 1px rgba($primary, 0.7);
        outline: 0;
    }
}
</style>
